import { useCallback, useEffect, useMemo, useState } from 'react';
import GlobalDatagrid from '../../../../../../components/mui/DataGrid/GlobalDatagrid';
import { AssessmentDetailsTableColumn } from './util/AssessmentDetailsTableColumn';
import GlobalWrapper from '../../../../../../components/Wrapper/GlobalWrapper';
import GlobalDialog from '../../../../../../components/mui/Dialogue/GlobalDialog';
import EditAssessmentDetails from './components/EditAssessmentDetails';
import GlobalSelectFieldWithoutFormik from '../../../../../../components/mui/GlobalSelectFieldWithoutFormik';
import Box from '@mui/material/Box';
import FromAndToDatePicker from '../../../../../../components/mui/FromAndToDatePicker';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { getAssessmentDetails, getCROlistUnderProject } from '../../../../../../redux/features/CRDslice';
import { EXAMINATION_TYPE } from './util/ExaminationType';
import dayjs from 'dayjs';
import { Autocomplete, TextField } from '@mui/material';
import { croTypeNames } from '../../../../../../utils/employeeTypes';
import { isSameUserType } from '../../../../../../utils/helperFunction';
import { FOUNDATION_DEPARTMENT_ID } from '../../../../../../utils/constants/departmentDetails';

export default function AssessmentDetails() {
  // Table and meta data
  const {
    loading,
    assessmentDetails: { tableData, meta },
  } = useSelector((state) => state.crd);

  const dispatch = useDispatch();

  const [fromDate, setFromDate] = useState(dayjs().startOf('year').format('YYYY-MM-DD'));
  const [toDate, setToDate] = useState(dayjs().endOf('year').format('YYYY-MM-DD'));
  const [type, setType] = useState({ id: '', value: '' });
  const [cro, setCro] = useState({ userID: '', name: '' });

  //---------------------- DataGrid State  ------------------------------
  const {
    paginationData: { gridSize: limit, pageNum: page, search },
  } = useSelector((state) => state.globalDataGrid);

  //---------------------- DispatchParams  ------------------------------
  const dispatchParams = useMemo(
    () => ({
      limit: limit ?? 10,
      page: page ?? 1,
      search: search ?? '',
      fromDate,
      toDate,
      type: type?.value === 'All' ? null : type?.value,
      cro_id: cro?.cro_id ?? null,
    }),
    [limit, page, search, fromDate, toDate, type, cro],
  );

  //---------------------- Global Modal ------------------------------
  const [openModalName, setOpenModalName] = useState(false);
  const [modalExtraData, setModalExtraData] = useState({});

  const MODAL_JSON = useMemo(
    () => [
      {
        title: 'Edit Assessment',
        content: (
          <EditAssessmentDetails
            rowData={modalExtraData}
            dispatchFn={getAssessmentDetails}
            dispatchParams={dispatchParams}
            setOpenModalName={setOpenModalName}
          />
        ),
      },
      {
        title: 'Duplicate Assessment',
        content: (
          <EditAssessmentDetails
            rowData={modalExtraData}
            dispatchFn={getAssessmentDetails}
            dispatchParams={dispatchParams}
            setOpenModalName={setOpenModalName}
            duplicate
          />
        ),
      },
    ],
    [modalExtraData, dispatchParams],
  );

  const getContentFromModalName = useCallback(
    (name) => {
      const modal = MODAL_JSON.find(({ title }) => title === name);
      return modal?.content ?? null;
    },
    [MODAL_JSON],
  );

  //-------------------------------------------------------------------

  const tableHead = useMemo(() => AssessmentDetailsTableColumn({ setOpenModalName, setModalExtraData }), []);

  const crosunderproject = useSelector((state) => state.crd.crosunderproject);

  const isCro = useMemo(() => {
    const profile = JSON.parse(localStorage.getItem('cred')).profile;
    return isSameUserType({ roleArray: croTypeNames, role: profile?.role });
  }, []);

  // useEffects
  useEffect(() => {
    dispatch(getAssessmentDetails(dispatchParams));
  }, [dispatch, dispatchParams]);

  useEffect(() => {
    if (!isCro) dispatch(getCROlistUnderProject(FOUNDATION_DEPARTMENT_ID));
  }, [isCro]);

  return (
    <GlobalWrapper title={'Assessment Details'}>
      <Box display={'flex'} gap={1} mb={1} flexDirection={{ xs: 'column', sm: 'row' }}>
        <FromAndToDatePicker fromDate={fromDate} toDate={toDate} setFromDate={setFromDate} setToDate={setToDate} />
        <Box display={'flex'} gap={1} width={'100%'} justifyContent={{ xs: 'space-between', md: 'flex-start' }}>
          <GlobalSelectFieldWithoutFormik
            sx={{
              minWidth: 100,
            }}
            data={EXAMINATION_TYPE}
            keys={{
              itemId: 'id',
              itemValue: 'label',
            }}
            label={'Type'}
            selectedDetails={type}
            setChange={setType}
          />
          {!isCro && (
            <Autocomplete
              options={[{ userID: null, name: 'All' }, ...crosunderproject]}
              getOptionKey={(e) => e.userID}
              getOptionLabel={(e) => e.name}
              loading={loading}
              onChange={(e, v) =>
                setCro((prev) => {
                  return { ...prev, cro_id: v?.userID ?? '' };
                })
              }
              sx={{ width: { xs: '100%', md: 300 } }}
              renderInput={(params) => <TextField {...params} label="CRO Name" />}
            />
          )}
        </Box>
      </Box>
      <GlobalDialog
        maxWidth={'100%'}
        title={openModalName}
        open={Boolean(openModalName)}
        setOpen={setOpenModalName}
        content={getContentFromModalName(openModalName)}
        closeButton
      />
      <GlobalDatagrid
        rows={tableData}
        tableHead={tableHead}
        meta={meta?.totalRecords}
        rowUniqueId={'slNo'}
        loading={loading}
      />
    </GlobalWrapper>
  );
}
